/**
 * The ONE `EnvironmentConfig` field table: every field's NEXT_PUBLIC binding AND its production-validation class in a
 * single keyed record, plus the three projections `components/lib/environment.ts` consumes (`buildServerConfig` /
 * `validateProdConfig`).
 *
 * WHY ONE RECORD AND NOT THREE LISTS. This leaf carried `CRITICAL_ENV_CONFIG_FIELDS`, `OPTIONAL_ENV_CONFIG_FIELDS` and
 * `ENV_VAR_CONFIG_MAP` as three hand-maintained sequences over the SAME key space, with nothing binding them to each
 * other or to `EnvironmentConfig`. Every per-field decision was therefore spelled in two places, and the disagreement
 * was already live rather than hypothetical: `environment` and `exploreHttp` were read from a var by the map and
 * classified by NEITHER list, so an empty value for either passed production validation silently. Keying the record by
 * `keyof EnvironmentConfig` and binding it with `satisfies` turns that class into a compile error -- a field added to
 * the interface without a binding no longer type-checks, which is the whole prevention.
 *
 * ORDER. `ENV_CONFIG_FIELDS` is declared in `buildServerConfig` read order, so the derived `ENV_VAR_CONFIG_MAP` keeps
 * the order it always had. The two classification projections now follow that same order rather than their former
 * hand-written one; both are consumed as membership sets (a filter over the config and a message list), so the order
 * they emit in is cosmetic and no consumer reads a position.
 */
import { ENV_NEXT_PUBLIC } from './env-var-names-app-config.ts';
import type { EnvironmentConfig, EnvConfigFieldBinding } from '@federa/types/frontend-runtime-config';

/**
 * The production-validation arms, which are what `validateProdConfig` branches on.
 * @runtime-derived-ssot-helper (const-leaf-pinned): the vocabulary is a DECISION, and `EnvConfigFieldBinding`
 * (`types/frontend-runtime-config.ts`) derives its member type from this record rather than respelling the literals.
 */
const ENV_CONFIG_VALIDATION = {
	/** Empty in production is an integrity FAILURE: `validateProdConfig` throws and the app does not boot. */
	CRITICAL: 'critical',
	/** Feature-specific: empty in production DEGRADES the outcome so the caller can surface it, but never blocks. */
	OPTIONAL: 'optional',
	/** Validated by no arm at all. Not a waiver granted here -- it RECORDS the state the three former lists hid, so a
	 *  field is unvalidated only where a reader can see it and say so. */
	UNVALIDATED: 'unvalidated',
} as const;

/** The validation arm of a field binding, derived from {@link ENV_CONFIG_VALIDATION} so the two cannot drift. */
export type EnvConfigValidation = (typeof ENV_CONFIG_VALIDATION)[keyof typeof ENV_CONFIG_VALIDATION];

/**
 * Field -> `{ envVar, validation }`, in `buildServerConfig` read order. The `satisfies Record<keyof EnvironmentConfig,
 * ...>` is the exhaustiveness gate: it is what makes a new interface field a type error here rather than a silently
 * unbound, silently unvalidated one at runtime.
 */
const ENV_CONFIG_FIELDS = {
	storageGatewayApi: { envVar: ENV_NEXT_PUBLIC.STORAGE_GATEWAY_API, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	conversationsApi: { envVar: ENV_NEXT_PUBLIC.CONVERSATIONS_API, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	getPropertyList: { envVar: ENV_NEXT_PUBLIC.PROPERTY_LIST_API, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	exploreDurableObjectApi: { envVar: ENV_NEXT_PUBLIC.EXPLORE_DURABLE_OBJECT, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	handleFilesUiApi: { envVar: ENV_NEXT_PUBLIC.HANDLE_FILES_API, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	chatRoomWS: { envVar: ENV_NEXT_PUBLIC.CHATROOM_WS, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	exploreWS: { envVar: ENV_NEXT_PUBLIC.EXPLORE_WS, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	stripePaymentIntent: { envVar: ENV_NEXT_PUBLIC.STRIPE_PAYMENT_INTENT, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	// UNVALIDATED, and inherited rather than chosen: neither former list named it. Reclassifying it changes what
	// production does on an empty value, so it is recorded honestly here and left to an owner who can run the gates.
	environment: { envVar: ENV_NEXT_PUBLIC.APP_ENV, validation: ENV_CONFIG_VALIDATION.UNVALIDATED },
	databaseName: { envVar: ENV_NEXT_PUBLIC.DATABASE_NAME, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	stripePublicKey: { envVar: ENV_NEXT_PUBLIC.STRIPE_PUBLIC_KEY, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	// UNVALIDATED, same inherited gap as `environment` above.
	exploreHttp: { envVar: ENV_NEXT_PUBLIC.EXPLORE_HTTP, validation: ENV_CONFIG_VALIDATION.UNVALIDATED },
	merchantIdentifier: { envVar: ENV_NEXT_PUBLIC.MERCHANT_IDENTIFIER, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	appUrl: { envVar: ENV_NEXT_PUBLIC.APP_URL, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	adminAppUrl: { envVar: ENV_NEXT_PUBLIC.ADMIN_APP_URL, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	firebaseApiKey: { envVar: ENV_NEXT_PUBLIC.FIREBASE_API_KEY, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseAuthDomain: { envVar: ENV_NEXT_PUBLIC.FIREBASE_AUTH_DOMAIN, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseProjectId: { envVar: ENV_NEXT_PUBLIC.FIREBASE_PROJECT_ID, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseStorageBucket: { envVar: ENV_NEXT_PUBLIC.FIREBASE_STORAGE_BUCKET, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseMessagingSenderId: { envVar: ENV_NEXT_PUBLIC.FIREBASE_MESSAGING_SENDER_ID, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseAppId: { envVar: ENV_NEXT_PUBLIC.FIREBASE_APP_ID, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	firebaseMeasurementId: { envVar: ENV_NEXT_PUBLIC.FIREBASE_MEASUREMENT_ID, validation: ENV_CONFIG_VALIDATION.OPTIONAL },
	staticApiToken: { envVar: ENV_NEXT_PUBLIC.STATIC_API_TOKEN, validation: ENV_CONFIG_VALIDATION.CRITICAL },
	// Supplied by NO NEXT_PUBLIC var: `buildServerConfig` seeds it undefined and a server-side path fills it in. It is
	// in the record anyway because that is what makes the key set exhaustive against `EnvironmentConfig`.
	storageGatewayApiToken: { envVar: null, validation: ENV_CONFIG_VALIDATION.UNVALIDATED },
} as const satisfies Record<keyof EnvironmentConfig, EnvConfigFieldBinding>;

/** The fields of {@link ENV_CONFIG_FIELDS} whose binding satisfies `predicate`, in declaration order. */
function envConfigFieldsWhere(predicate: (binding: EnvConfigFieldBinding) => boolean): Array<keyof EnvironmentConfig> {
	const fields = Object.keys(ENV_CONFIG_FIELDS) as Array<keyof EnvironmentConfig>;
	return fields.filter((field) => predicate(ENV_CONFIG_FIELDS[field]));
}

/** Fields that must not be empty in production. */
export const CRITICAL_ENV_CONFIG_FIELDS: Array<keyof EnvironmentConfig> = envConfigFieldsWhere(
	(binding) => binding.validation === ENV_CONFIG_VALIDATION.CRITICAL,
);

/** Fields that are feature-specific; warn if empty but don't block. */
export const OPTIONAL_ENV_CONFIG_FIELDS: Array<keyof EnvironmentConfig> = envConfigFieldsWhere(
	(binding) => binding.validation === ENV_CONFIG_VALIDATION.OPTIONAL,
);

/** One config field paired with the NEXT_PUBLIC env var that supplies it. */
type ConfigFieldEnvVarPair = [keyof EnvironmentConfig, string];

/** Config-field to NEXT_PUBLIC env-var binding, in `buildServerConfig` read order. Fields no env var supplies are
 *  absent, which is what keeps this projection exactly the loop `buildServerConfig` runs. */
export const ENV_VAR_CONFIG_MAP: ConfigFieldEnvVarPair[] = (
	Object.keys(ENV_CONFIG_FIELDS) as Array<keyof EnvironmentConfig>
).flatMap((field): ConfigFieldEnvVarPair[] => {
	// The null arm is DROPPED rather than defaulted: a `?? ''` here would read as a live fallback while being
	// unreachable, and would hand `getEnv` an empty var name if it ever became reachable.
	const { envVar } = ENV_CONFIG_FIELDS[field];
	return envVar === null ? [] : [[field, envVar]];
});
